import { useEffect, useState, type ReactNode } from 'react';
import { IonSpinner } from '@ionic/react';
import { cn } from '@shared/utils';

export interface ProgressOverlayProps {
  /** 疊在轉圈中央的小圖示，說明正在做的是哪一類事 */
  icon?: ReactNode;
  title: string;
  /** 預期要等多久、在做什麼 */
  message: string;
  /** 超過 `slowAfterSeconds` 仍未結束時，換成這句話 */
  slowMessage?: string;
  slowAfterSeconds?: number;
}

/**
 * 蓋住整個畫面的進行中遮罩，用在等待期間任何操作都會被結果覆蓋的情況。
 *
 * 遮罩同時做兩件事：擋住捲動與誤觸，讓「現在不能動」看得出來而不是試了才發現；
 * 以及給進度感，因為幾秒鐘只有一個轉圈圈會被感受成壞掉了。
 *
 * 只講真話，不做假的階段進度：拿不到真實進度時，編造的階段在猜錯時會直接說謊。
 * 改成說明大概要多久，以及超時後仍在處理。
 *
 * 由呼叫端以 `isBusy && <ProgressOverlay />` 掛載，所以每次重新開始都會重新計時。
 */
export function ProgressOverlay({
  icon,
  title,
  message,
  slowMessage,
  slowAfterSeconds = 8,
}: ProgressOverlayProps) {
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    if (!slowMessage) return;

    const timer = setTimeout(() => setIsSlow(true), slowAfterSeconds * 1000);
    return () => clearTimeout(timer);
  }, [slowMessage, slowAfterSeconds]);

  return (
    /* fixed 而非 absolute：連面板頂端的按鈕一起蓋住，「明確被遮住」比「看得到卻按不動」清楚 */
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'bg-background/80 backdrop-blur-sm',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex max-w-[16rem] flex-col items-center gap-3 px-6 text-center">
        <div className="relative flex size-12 items-center justify-center text-accent">
          <IonSpinner name="crescent" className="absolute size-12" />
          {icon}
        </div>

        <p className="text-sm">{title}</p>

        <p className="text-xs leading-relaxed text-muted-foreground">
          {isSlow ? slowMessage : message}
        </p>
      </div>
    </div>
  );
}
